/**
 * Shared plumbing for the entity views.
 *
 * `entityviews.ts` accumulated a small toolbox — reference-counted GPU
 * bundles, limb chains, geometry merging, vertex-attribute baking — that every
 * kind needs. It lives here so each of the canonical entities can own a file
 * without one of them also owning the tools the others borrow.
 *
 * Nothing in here knows what a monster is. Keep it that way: a helper that
 * mentions a specific kind belongs in that kind's file.
 */

import * as THREE from 'three';
import { angleDelta, clamp01, lerp } from '../core/math';
import type { EntityState } from '../core/types';

export interface Disposable {
  dispose(): void;
}

/**
 * A lazily built, reference-counted bundle of GPU resources for one kind.
 * The bundle is freed when the last view holding it is disposed, so a level
 * change that removes every hound also frees the hound's buffers.
 */
export class Kit<T extends Disposable> {
  private value: T | null = null;
  private refs = 0;

  constructor(private readonly build: () => T) {}

  acquire(): T {
    if (this.value === null) this.value = this.build();
    this.refs++;
    return this.value;
  }

  release(): void {
    if (this.refs === 0) return;
    if (--this.refs === 0 && this.value !== null) {
      this.value.dispose();
      this.value = null;
    }
  }
}

/** Dispose a batch of geometries/materials/textures in one call. */
export function freeAll(items: readonly Disposable[]): void {
  for (const item of items) item.dispose();
}

/** Exponential smoothing on an angle, taking the short way round. */
export function dampAngle(current: number, target: number, rate: number, dt: number): number {
  return current + angleDelta(current, target) * (1 - Math.exp(-rate * dt));
}

/** Planar speed. Gait frequency and stride length both key off this. */
export function planarSpeed(state: EntityState): number {
  return Math.hypot(state.vel.x, state.vel.z);
}

/** Every mesh under `root` casts. Shadows are what put a figure on the floor. */
export function castShadows(root: THREE.Object3D): void {
  root.traverse((o) => {
    o.castShadow = true;
  });
}

/* ------------------------------------------------------------------ *
 * Geometry helpers
 * ------------------------------------------------------------------ */

/** One tapered limb bone, hanging from its joint at y=0 down to y=-length. */
export function bone(length: number, top: number, bottom: number, sides = 5): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(top, bottom, length, sides, 1);
  g.translate(0, -length / 2, 0);
  return g;
}

/**
 * The same, with the end caps left off. A limb thinner than a finger never
 * shows its ends, and dropping them halves the triangles — which matters on
 * the kinds that have twenty of them.
 */
export function thinBone(length: number, top: number, bottom: number, sides = 3): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(top, bottom, length, sides, 1, true);
  g.translate(0, -length / 2, 0);
  return g;
}

/**
 * Nested joint groups, one per bone. Rotating `joints[i]` bends the limb at
 * that joint and carries everything below it. `joints[0]` is the limb root and
 * is the only one the caller needs to parent.
 */
export function chain(
  geos: readonly THREE.BufferGeometry[],
  lengths: readonly number[],
  material: THREE.Material
): THREE.Group[] {
  const joints: THREE.Group[] = [];
  for (let i = 0; i < geos.length; i++) {
    const joint = new THREE.Group();
    joint.add(new THREE.Mesh(geos[i]!, material));
    if (i > 0) {
      joint.position.y = -lengths[i - 1]!;
      joints[i - 1]!.add(joint);
    }
    joints.push(joint);
  }
  return joints;
}

/** Squeeze a geometry toward its -Z end, so a box reads as a snout. */
export function taperZ(
  geo: THREE.BufferGeometry,
  nearZ: number,
  farZ: number,
  scale: number
): THREE.BufferGeometry {
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const t = clamp01((pos.getZ(i) - nearZ) / (farZ - nearZ));
    const s = lerp(1, scale, t);
    pos.setX(i, pos.getX(i) * s);
    pos.setY(i, pos.getY(i) * s);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

/**
 * Concatenate geometries into one buffer, position and normal only. Used where
 * a figure never articulates, so it can be drawn in a single call and carry a
 * single continuous vertex-alpha ramp across its parts.
 *
 * Consumes its inputs: every geometry passed in is disposed.
 */
export function mergeParts(parts: readonly THREE.BufferGeometry[]): THREE.BufferGeometry {
  const flat = parts.map((p) => (p.index ? p.toNonIndexed() : p));
  let count = 0;
  for (const p of flat) count += p.attributes.position.count;

  const position = new Float32Array(count * 3);
  const normal = new Float32Array(count * 3);
  let offset = 0;
  for (const p of flat) {
    // Everything reaching here is built above from Float32, non-interleaved.
    const pa = p.attributes.position as THREE.BufferAttribute;
    const na = p.attributes.normal as THREE.BufferAttribute;
    position.set(pa.array as Float32Array, offset * 3);
    normal.set(na.array as Float32Array, offset * 3);
    offset += pa.count;
  }

  for (let i = 0; i < flat.length; i++) {
    flat[i]!.dispose();
    if (flat[i] !== parts[i]) parts[i]!.dispose();
  }

  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(position, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(normal, 3));
  return out;
}

/**
 * Bake a per-vertex alpha channel. three enables `USE_COLOR_ALPHA` for a
 * 4-component colour attribute, which gets us a dissolving silhouette without
 * a custom shader.
 */
export function bakeAlpha(
  geo: THREE.BufferGeometry,
  alphaAt: (x: number, y: number, z: number) => number
): THREE.BufferGeometry {
  const pos = geo.attributes.position;
  const rgba = new Float32Array(pos.count * 4);
  for (let i = 0; i < pos.count; i++) {
    rgba[i * 4] = 1;
    rgba[i * 4 + 1] = 1;
    rgba[i * 4 + 2] = 1;
    rgba[i * 4 + 3] = clamp01(alphaAt(pos.getX(i), pos.getY(i), pos.getZ(i)));
  }
  geo.setAttribute('color', new THREE.BufferAttribute(rgba, 4));
  return geo;
}

/**
 * Bake per-vertex colour, so one buffer and one material can carry a marking —
 * a stripe down a back, a band across a wing — that would otherwise cost a
 * second mesh or a texture.
 *
 * `colorAt` returns a colour the caller owns and may reuse; it is copied out
 * immediately. Work in the linear working space (`THREE.Color.setHex` converts
 * from sRGB for you): three does not colour-manage vertex attributes.
 */
export function bakeRGB(
  geo: THREE.BufferGeometry,
  colorAt: (x: number, y: number, z: number) => THREE.Color
): THREE.BufferGeometry {
  const pos = geo.attributes.position;
  const rgb = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const c = colorAt(pos.getX(i), pos.getY(i), pos.getZ(i));
    rgb[i * 3] = c.r;
    rgb[i * 3 + 1] = c.g;
    rgb[i * 3 + 2] = c.b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(rgb, 3));
  return geo;
}
